import { motion } from "framer-motion";
import { Minus, Plus, Trash2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export const CartItem = ({ item, lang, updateQty, removeItem }) => (
  <li className="flex gap-4" data-testid={`cart-item-${item.id}`}>
    <div className="w-20 h-24 rounded-xl overflow-hidden shrink-0" style={{ backgroundColor: item.bgTint }}>
      <img loading="lazy" decoding="async" src={item.image} alt={item.name[lang]} className="w-full h-full object-cover" />
    </div>
    <div className="flex-1">
      <div className="flex justify-between gap-2">
        <h3 className="font-serif text-lg text-stone-900 leading-tight">{item.name[lang]}</h3>
        <button data-testid={`remove-item-${item.id}`} onClick={() => removeItem(item.id)} className="text-stone-400 hover:text-[#C85A32] transition-colors"><Trash2 size={15} /></button>
      </div>
      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-stone-500">{item.weight}</p>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center rounded-full border border-stone-300">
          <button data-testid={`qty-minus-${item.id}`} onClick={() => updateQty(item.id, -1)} className="w-8 h-8 flex items-center justify-center hover:text-[#C85A32]"><Minus size={13} /></button>
          <span data-testid={`qty-value-${item.id}`} className="w-6 text-center font-mono text-sm">{item.qty}</span>
          <button data-testid={`qty-plus-${item.id}`} onClick={() => updateQty(item.id, 1)} className="w-8 h-8 flex items-center justify-center hover:text-[#4A624E]"><Plus size={13} /></button>
        </div>
        <span className="font-mono text-sm text-stone-900">{(item.price * item.qty).toFixed(0)} CHF</span>
      </div>
    </div>
  </li>
);

export const FreeShippingBar = ({ totals, config, tc }) => {
  const remaining = Math.max(0, config.free_shipping_threshold - (totals.subtotal - totals.discount));
  const progress = Math.min(100, ((totals.subtotal - totals.discount) / config.free_shipping_threshold) * 100);
  return (
    <div className="mb-6" data-testid="cart-free-shipping-bar">
      <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-stone-600 mb-2">
        {totals.shipping === 0 ? tc.freeShippingReached : tc.freeShippingLeft(remaining.toFixed(0))}
      </p>
      <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden">
        <motion.div animate={{ width: `${progress}%` }} transition={{ duration: 0.6 }} className="h-full bg-[#4A624E] rounded-full" />
      </div>
    </div>
  );
};

export const CrossSell = ({ suggestions, lang, t, addToCart }) => {
  if (suggestions.length === 0) return null;
  return (
    <div className="mt-8" data-testid="cart-cross-sell">
      <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-3">{t.crossSell.title}</p>
      <div className="grid grid-cols-2 gap-3">
        {suggestions.map((p) => (
          <div key={p.id} className="rounded-xl border border-stone-200 bg-white p-3 flex flex-col" data-testid={`cross-sell-${p.id}`}>
            <div className="rounded-lg overflow-hidden aspect-[4/5] mb-2" style={{ backgroundColor: p.bgTint }}>
              <img src={p.image} alt={p.name[lang]} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <p className="font-serif text-sm text-stone-900 leading-tight flex-1">{p.name[lang]}</p>
            <div className="mt-2 flex items-center justify-between">
              <span className="font-mono text-xs">{p.price} CHF</span>
              <button data-testid={`cross-sell-add-${p.id}`} onClick={() => { addToCart(p.id); toast.success(`${p.name[lang]} — ${t.products.added}`); }} className="rounded-full bg-stone-900 text-white px-3 py-1 text-[10px] uppercase tracking-wide hover:bg-[#C85A32] transition-colors">{t.crossSell.add}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CartSummary = ({ totals, promo, tc, trust, onCheckout, submitting }) => (
  <div className="border-t border-stone-200 px-6 py-5 space-y-2 text-sm" data-testid="cart-summary">
    <div className="flex justify-between text-stone-600"><span>{tc.subtotal}</span><span className="font-mono">{totals.subtotal.toFixed(2)} CHF</span></div>
    {totals.discount > 0 && (
      <div className="flex justify-between text-[#4A624E]"><span>{tc.discount} ({promo})</span><span className="font-mono">−{totals.discount.toFixed(2)} CHF</span></div>
    )}
    <div className="flex justify-between text-stone-600"><span>{tc.shipping}</span><span className="font-mono">{totals.shipping === 0 ? tc.free : `${totals.shipping.toFixed(2)} CHF`}</span></div>
    <div className="flex justify-between text-stone-900 font-medium pt-2 border-t border-stone-200"><span>{tc.total}</span><span className="font-mono" data-testid="cart-total">{totals.total.toFixed(2)} CHF</span></div>
    <button
      data-testid="checkout-button"
      onClick={onCheckout}
      disabled={submitting}
      className="mt-3 w-full rounded-full bg-stone-900 text-[#FAF7F2] py-4 text-[12px] uppercase tracking-[0.2em] hover:bg-[#C85A32] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
    >
      <Lock size={13} /> {submitting ? "..." : tc.checkout}
    </button>
    <ul className="pt-2 space-y-1" data-testid="cart-trust">
      {trust.map((x) => <li key={x} className="flex items-center gap-2 text-[11px] text-stone-500"><ShieldCheck size={12} className="text-[#4A624E]" /> {x}</li>)}
    </ul>
  </div>
);
